import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import AnalysisReport, { ReportSkeleton } from '../components/AnalysisReport';
import ComparisonReport from '../components/ComparisonReport';
import BulkReport from '../components/BulkReport';
import MobileReport from '../components/MobileReport';
import ReportFooter from '../components/ReportFooter';
import PdfDownloadButton from '../components/PdfDownloadButton';
import { computeAnalysis, computeBulkAnalysis, optsForUnit, optsFromUnit, strOptsFor, unitHasStr, aedStr, ilsStr, monthYear, RESALE_DEFAULTS, areaStr } from '../lib/analysisEngine';
import { exportElementToPdf, exportMobileElementToPdf, pdfFilename } from '../lib/pdfExport';
import { StrVsLtr } from '../components/StrAnalysis';
import { STR_CONFIG_FALLBACK, strDefaults, furnishingPreset } from '../lib/strConfig';
import AnalysisAssistant from '../components/AnalysisAssistant';
import { buildAnalysisContext } from '../lib/aiContext';

const API = 'https://affinityconcept.tech/api';
const ILS_FALLBACK = 0.95;
const EUR_FALLBACK = 0.25;
const USD_FALLBACK = 0.27;

const EMPTY_UNIT = {
  unit_type: '1BR', unit_number: '', price_aed: '', internal_sqft: '', balcony_sqft: '',
  service_charge_rate: 14, expected_rent_yearly_aed: '',
  market_price_per_sqft: '', market_value_aed: '',
  is_fully_furnished: false, furnishing_cost: '', str_adr: '', str_occupancy: '', notes: '',
};
const EMPTY_MILESTONE = { milestone_label: '', percentage: '', due_date: '', months_from_booking: '', is_handover: false, is_post_handover: false };
const EMPTY_FORM = {
  developer_name: '', project_name: '', area: '', emirate: 'dubai',
  handover_date: '', description: '', admin_fees: 5000, dld_enabled: true, is_phpp: false,
  hero_image_url: '',
  units: [{ ...EMPTY_UNIT }],
  payment_plans: [
    { milestone_label: 'Booking', percentage: 20, due_date: '', months_from_booking: 0, is_handover: false, is_post_handover: false },
    { milestone_label: 'During Construction', percentage: 40, due_date: '', months_from_booking: 6, is_handover: false, is_post_handover: false },
    { milestone_label: 'Handover', percentage: 40, due_date: '', months_from_booking: '', is_handover: true, is_post_handover: false },
  ],
};
const UNIT_TYPES = ['Studio', '1BR', '2BR', '3BR', '4BR', 'Penthouse', 'Townhouse', 'Villa', 'Office'];
// Mirrors the backend's DEFAULT_ASSUMPTIONS_TEMPLATE shape — used only as a fallback
// before /analysis/defaults has loaded (e.g. opening the Defaults screen very early).
const DEFAULT_ASSUMPTIONS_TEMPLATE = {
  cap_bands: [8, 5, 3], rent_bands: [5, 3, 2], maturity_years: 10, exit_year: null,
  strategy: 'ltr', dld_enabled: true, dld_rate: 4.0, admin_fees: 0,
  str_expenses: { mgmtPct: 20, dewaMonthly: 800, gasMonthly: 100, internetMonthly: 300,
    chillerMonthly: 500, dtcmLicenseYearly: 1500, tourismPerNight: 15, furnitureRefreshPct: 10, miscYearly: 0 },
  resale: { enabled: false, commissionPct: 2, nocFee: 5250 },
  rate_mode: 'live', manual_ils: null,
};

// strDefaults(config) returns the full str_config.json `str_defaults` blob, which
// bundles a suggested `occupancy` alongside the real expense presets (it's reused to
// seed a brand-new unit's occupancy). Occupancy has its OWN dedicated state/input in
// this page though — strip it here before it ever lands in `strExp`, which must hold
// ONLY the expense-preset fields. Letting a stray `strExp.occupancy` survive is exactly
// what let a stale copy silently override the live occupancy value (see strParams below).
function strExpenseDefaults(config) {
  const { occupancy, ...rest } = strDefaults(config);
  return rest;
}

// coerce all values of a params object to numbers (form inputs arrive as strings)
function numify(obj) {
  const out = {};
  for (const k in obj) out[k] = Number(obj[k]) || 0;
  return out;
}

// advance a 'YYYY-MM-DD' string by n months (keeps day-of-month where possible)
function addMonthsStr(str, n) {
  if (!str) return str;
  const [y, m, d] = str.split('-').map(Number);
  const dt = new Date(y, (m - 1) + n, d);
  const p = (x) => String(x).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
}

export default function ProjectAnalysis({ token, onLogout }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selProjectId, setSelProjectId] = useState(null);
  const [selUnitId, setSelUnitId] = useState(null);
  const [mode, setMode] = useState('single');   // 'single' | 'compare'

  const [ils, setIls] = useState(ILS_FALLBACK);
  const [eur, setEur] = useState(EUR_FALLBACK);
  const [usd, setUsd] = useState(USD_FALLBACK);
  // EUR/USD conversion lines are optional here too — default OFF (AED + ₪ only),
  // remembered per browser. No manual-override mode (unlike ILS): live rate only.
  const [showEur, setShowEur] = useState(() => {
    try { return localStorage.getItem('affinity.analysis.showEur') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('affinity.analysis.showEur', showEur ? '1' : '0'); } catch { /* ignore */ } }, [showEur]);
  const [showUsd, setShowUsd] = useState(() => {
    try { return localStorage.getItem('affinity.analysis.showUsd') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('affinity.analysis.showUsd', showUsd ? '1' : '0'); } catch { /* ignore */ } }, [showUsd]);
  const [ilsLive, setIlsLive] = useState(false);
  const [ilsUpdated, setIlsUpdated] = useState('');       // last-updated time from the live API
  // Manual AED→ILS override: 'live' uses the fetched rate, 'manual' pins a typed rate.
  // Persisted per unit (rate_mode/manual_ils) alongside the rest of the assumptions bundle.
  const [rateMode, setRateMode] = useState('live');
  const [manualIls, setManualIls] = useState('');         // ILS per 1 AED (string, from the input)

  // ---- Per-unit assumptions & fees ("Adjust assumptions & fees" panel) ----
  // Every field below is scoped to the currently selected unit: loaded from
  // unit.assumptions on unit switch, auto-saved back (debounced) to that same unit's
  // record on edit. Never shared across units/projects — that was the bug.
  const [dldEnabled, setDldEnabled] = useState(true);
  const [dldRate, setDldRate] = useState(4);
  const [adminFees, setAdminFees] = useState(0);
  const [scRate, setScRate] = useState(14);
  const [startRent, setStartRent] = useState(0);
  const [capBands, setCapBands] = useState([8, 5, 3]);
  const [rentBands, setRentBands] = useState([5, 3, 2]);
  const [maturityYears, setMaturityYears] = useState(10);
  const [exitYear, setExitYear] = useState(null);
  const uaLoadedFor = useRef(null);       // unit id the fields above currently reflect
  const uaSaveTimer = useRef(null);       // debounce handle for the autosave PUT
  const [uaSaving, setUaSaving] = useState(false);

  // ---- Short-term rental (holiday-home) ----
  const [strConfig, setStrConfig] = useState(STR_CONFIG_FALLBACK);
  const [strategy, setStrategy] = useState('ltr');           // 'ltr' | 'str' | 'compare' — saved per unit
  const [adr, setAdr] = useState(0);                         // AED/night
  const [occupancy, setOccupancy] = useState(75);            // %
  const [furnishingCost, setFurnishingCost] = useState(0);   // resolved for the selected unit
  const [strExp, setStrExp] = useState(strExpenseDefaults(STR_CONFIG_FALLBACK));

  // Exit-scenario resale costs (broker commission % + flat NOC fee) — per unit; saved
  // to the unit's own assumptions. Snapshotted into shares.
  const [resale, setResale] = useState(RESALE_DEFAULTS);

  // Platform-level default template (used to seed brand-new bulk deals + the Defaults
  // settings screen). Editing it never retroactively changes existing units.
  const [platformDefaults, setPlatformDefaults] = useState(null);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [defaultsForm, setDefaultsForm] = useState(null);
  const [defaultsSaving, setDefaultsSaving] = useState(false);

  // ---- Compare mode: per-unit assumptions by default; optional shared override ----
  const [compareStrategy, setCompareStrategy] = useState('ltr');   // 'ltr' | 'str' | 'compare' — applies to the whole comparison
  const [compareShared, setCompareShared] = useState(false);   // off by default — units are independent
  const [compareCapBands, setCompareCapBands] = useState([8, 5, 3]);
  const [compareRentBands, setCompareRentBands] = useState([5, 3, 2]);
  const [compareMaturityYears, setCompareMaturityYears] = useState(10);

  // ---- Bulk deal: its own shared assumption set, stored on the deal record ----
  const [bulkCapBands, setBulkCapBands] = useState([8, 5, 3]);
  const [bulkRentBands, setBulkRentBands] = useState([5, 3, 2]);
  const [bulkMaturityYears, setBulkMaturityYears] = useState(10);
  const [bulkResale, setBulkResale] = useState(RESALE_DEFAULTS);

  // Lite / Advanced report view (internal page defaults to Advanced). Remembered per session.
  const [view, setView] = useState(() => {
    try { return sessionStorage.getItem('affinity.analysis.view.internal') || 'advanced'; } catch { return 'advanced'; }
  });
  useEffect(() => { try { sessionStorage.setItem('affinity.analysis.view.internal', view); } catch { /* ignore */ } }, [view]);

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});   // { project_name, plan }

  // sticky mini-header + custom confirm dialog
  const mainRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [confirmState, setConfirmState] = useState(null); // { title, message, confirmLabel, onConfirm }

  // share + pdf + compare
  const reportRef = useRef(null);
  const mobileRef = useRef(null);
  const compareRef = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLang, setShareLang] = useState('en');   // default language baked into the share snapshot
  const [shareAllowQuestions, setShareAllowQuestions] = useState(false);  // "Ask about this deal" on the client link — off by default
  const [shares, setShares] = useState([]);
  // "Ask about this deal" — lets the chat assistant drive the Exit Scenario slider;
  // null until the chat sets it, so the card stays uncontrolled (normal drag) until then.
  const [chatExitYear, setChatExitYear] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [compareSel, setCompareSel] = useState([]);       // unit ids
  const [rentOverrides, setRentOverrides] = useState({}); // unitId -> yearly rent

  // ---------- bulk deal (package of several units) ----------
  const [bulkDeals, setBulkDeals] = useState([]);         // saved deals
  const [bulkDealId, setBulkDealId] = useState(null);     // loaded deal id (null => unsaved)
  const [bulkName, setBulkName] = useState('');
  const [bulkLines, setBulkLines] = useState([]);         // [{ unit_id, quantity, price_override, rent_override }]
  const [bulkDiscountPct, setBulkDiscountPct] = useState('');
  const [bulkStrategy, setBulkStrategy] = useState('ltr');// 'ltr' | 'str'
  const [bulkSaving, setBulkSaving] = useState(false);
  const bulkRef = useRef(null);
  const bulkMobileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data)).catch(() => {});
    fetchProjects();
    axios.get('https://open.er-api.com/v6/latest/AED')
      .then(r => {
        const rate = r.data?.rates?.ILS; if (rate) { setIls(rate); setIlsLive(true); setIlsUpdated(r.data?.time_last_update_utc || ''); }
        const eurRate = r.data?.rates?.EUR; if (eurRate) setEur(eurRate);
        const usdRate = r.data?.rates?.USD; if (usdRate) setUsd(usdRate);
      })
      .catch(() => { setIls(ILS_FALLBACK); setIlsLive(false); setEur(EUR_FALLBACK); setUsd(USD_FALLBACK); });
    axios.get(`${API}/analysis/str-config`, { headers })
      .then(r => { if (r.data) { setStrConfig(r.data); setStrExp(strExpenseDefaults(r.data)); } })
      .catch(() => {});
    axios.get(`${API}/analysis/defaults`, { headers }).then(r => setPlatformDefaults(r.data)).catch(() => {});
    fetchBulkDeals();
  }, []);   // eslint-disable-line

  function fetchBulkDeals() {
    axios.get(`${API}/analysis/bulk-deals`, { headers }).then(r => setBulkDeals(r.data)).catch(() => {});
  }

  function fetchProjects() {
    setLoading(true);
    axios.get(`${API}/analysis/projects`, { headers })
      .then(r => {
        setProjects(r.data);
        if (r.data.length && selProjectId == null) {
          setSelProjectId(r.data[0].id);
          setSelUnitId(r.data[0].units[0]?.id ?? null);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }

  const project = useMemo(() => projects.find(p => p.id === selProjectId) || null, [projects, selProjectId]);
  const unit = useMemo(() => project?.units.find(u => u.id === selUnitId) || project?.units[0] || null, [project, selUnitId]);
  const allUnits = useMemo(() => projects.flatMap(p => p.units.map(u => ({ project: p, unit: u }))), [projects]);

  // Load this unit's OWN saved assumptions whenever the selection changes. Runs before
  // the autosave effect below (declared first) and marks uaLoadedFor so that effect
  // knows the fields now reflect this unit and can start watching for edits.
  useEffect(() => {
    if (!project || !unit) return;
    uaLoadedFor.current = null;   // block the autosave effect while we repopulate
    if (uaSaveTimer.current) { clearTimeout(uaSaveTimer.current); uaSaveTimer.current = null; }
    setChatExitYear(null);   // back to the card's own default for the newly-selected unit
    const ua = unit.assumptions || {};
    const cap = ua.cap_bands || [8, 5, 3];
    const rent = ua.rent_bands || [5, 3, 2];
    const se = { ...strExpenseDefaults(strConfig), ...(ua.str_expenses || {}) };
    const rs = ua.resale || RESALE_DEFAULTS;
    setDldEnabled(ua.dld_enabled !== undefined ? ua.dld_enabled : true);
    setDldRate(ua.dld_rate || 4);
    setAdminFees(ua.admin_fees || 0);
    setScRate(unit.service_charge_rate || 14);
    setStartRent(unit.expected_rent_yearly_aed || 0);
    setCapBands(cap);
    setRentBands(rent);
    setMaturityYears(ua.maturity_years || 10);
    setExitYear(ua.exit_year ?? null);
    setStrategy(ua.strategy || 'ltr');
    setAdr(unit.str_adr || 0);
    setOccupancy(unit.str_occupancy || strDefaults(strConfig).occupancy);
    setStrExp(se);
    setResale({ enabled: !!rs.enabled, commissionPct: rs.commissionPct ?? 2, nocFee: rs.nocFee ?? 5250 });
    setRateMode(ua.rate_mode || 'live');
    setManualIls(ua.manual_ils != null ? String(ua.manual_ils) : '');
    // furnishing cost: unit value if set, else preset by type + furnished status
    const fc = (unit.furnishing_cost != null && unit.furnishing_cost !== '')
      ? unit.furnishing_cost : furnishingPreset(unit.unit_type, unit.is_fully_furnished, strConfig);
    setFurnishingCost(fc);
    // mark loaded on the next tick so this same render's state doesn't get echoed back
    // as an "edit" by the autosave effect
    const t = setTimeout(() => { uaLoadedFor.current = unit.id; }, 0);
    return () => clearTimeout(t);
  }, [selProjectId, selUnitId, strConfig]);   // eslint-disable-line

  // Auto-save (debounced) — any edit to this unit's assumptions panel persists to THAT
  // unit's own record only. Guarded by uaLoadedFor so it never fires during the load
  // effect above, and never writes a stale unit's edits onto a newly-selected unit.
  useEffect(() => {
    if (uaLoadedFor.current == null || uaLoadedFor.current !== selUnitId) return;
    const targetUnitId = selUnitId;
    if (uaSaveTimer.current) clearTimeout(uaSaveTimer.current);
    uaSaveTimer.current = setTimeout(async () => {
      setUaSaving(true);
      const body = {
        service_charge_rate: Number(scRate) || 0,
        expected_rent_yearly_aed: Number(startRent) || 0,
        str_adr: Number(adr) || 0,
        str_occupancy: Number(occupancy) || 0,
        furnishing_cost: Number(furnishingCost) || 0,
        cap_bands: capBands, rent_bands: rentBands,
        maturity_years: Number(maturityYears) || 10,
        exit_year: exitYear,
        strategy, dld_enabled: dldEnabled, dld_rate: Number(dldRate) || 4,
        admin_fees: Number(adminFees) || 0,
        str_expenses: numify(strExp),
        resale: { enabled: !!resale.enabled, commissionPct: Number(resale.commissionPct) || 0, nocFee: Number(resale.nocFee) || 0 },
        rate_mode: rateMode, manual_ils: manualIls === '' ? null : Number(manualIls),
      };
      try {
        const { data } = await axios.put(`${API}/analysis/units/${targetUnitId}/assumptions`, body, { headers });
        // reflect the save in the cached project list (keeps compare/bulk views fresh
        // without a full refetch) — only if the selection hasn't moved on meanwhile.
        setProjects(ps => ps.map(p => p.id !== data.project_id ? p : { ...p, units: p.units.map(u => u.id === data.id ? data : u) }));
      } catch { /* best-effort autosave */ }
      setUaSaving(false);
    }, 600);
    return () => clearTimeout(uaSaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selUnitId, dldEnabled, dldRate, adminFees, scRate, startRent, capBands, rentBands, maturityYears,
      exitYear, strategy, adr, occupancy, furnishingCost, strExp, resale, rateMode, manualIls]);

  async function resetUnitToDefaults() {
    if (!unit) return;
    try {
      const { data } = await axios.post(`${API}/analysis/units/${unit.id}/assumptions/reset`, {}, { headers });
      setProjects(ps => ps.map(p => p.id !== data.project_id ? p : { ...p, units: p.units.map(u => u.id === data.id ? data : u) }));
      flash('Reset to platform defaults ✓');
    } catch { flash('Reset failed'); }
  }

  // Effective AED→ILS rate applied to every ₪ figure: the manual override when active
  // (and valid), otherwise the live/fallback rate. Changing it re-renders the reports
  // instantly — the engine doesn't use ILS, so no recompute is needed.
  const effIls = (rateMode === 'manual' && Number(manualIls) > 0) ? Number(manualIls) : ils;
  // EUR/USD have no manual-override mode — live/fallback rate, or null when hidden.
  const effEur = showEur ? eur : null;
  const effUsd = showUsd ? usd : null;
  const rateOutOfBounds = rateMode === 'manual' && manualIls !== '' && (Number(manualIls) < 0.5 || Number(manualIls) > 1.5);

  // ---- Platform Defaults screen (low priority) ----
  function openDefaults() {
    setDefaultsForm(platformDefaults ? JSON.parse(JSON.stringify(platformDefaults)) : JSON.parse(JSON.stringify(DEFAULT_ASSUMPTIONS_TEMPLATE)));
    setDefaultsOpen(true);
  }
  async function saveDefaults() {
    setDefaultsSaving(true);
    try {
      const { data } = await axios.put(`${API}/analysis/defaults`, defaultsForm, { headers });
      setPlatformDefaults(data);
      setDefaultsOpen(false);
      flash('Platform defaults saved ✓ (existing units unchanged)');
    } catch { flash('Save failed'); }
    setDefaultsSaving(false);
  }

  // switch to manual → prefill with the current live rate so it can be nudged, not retyped
  function enableManualRate() {
    if (manualIls === '' || manualIls == null) setManualIls(Number(ils).toFixed(4));
    setRateMode('manual');
  }

  const assumptions = { dldEnabled, dldRate: Number(dldRate) || 4, adminFees: Number(adminFees) || 0, scRate: Number(scRate), startRent: Number(startRent) || 0, capBands, rentBands, maturityYears };
  // adr/occupancy spread LAST — they're the single source of truth (their own inputs,
  // reloaded/saved per unit) and must always win over whatever strExp carries, even if a
  // stray key ever ends up in there again.
  const strParams = { ...numify(strExp), adr: Number(adr) || 0, occupancy: Number(occupancy) || 0 };

  // LTR analysis (existing behaviour) + STR overlay for the same unit/assumptions.
  const analysis = useMemo(
    () => (project && unit) ? computeAnalysis(project, unit, assumptions) : null,
    [project, unit, dldEnabled, dldRate, adminFees, scRate, startRent, capBands, rentBands, maturityYears]   // eslint-disable-line
  );
  const analysisStr = useMemo(
    () => (project && unit) ? computeAnalysis(project, unit, { ...assumptions, strategy: 'str', furnishingCost: Number(furnishingCost) || 0, str: strParams }) : null,
    [project, unit, dldEnabled, dldRate, adminFees, scRate, startRent, capBands, rentBands, maturityYears, furnishingCost, adr, occupancy, strExp]   // eslint-disable-line
  );
  const shownAnalysis = strategy === 'ltr' ? analysis : analysisStr;
  // Exact engine opts behind the shown analysis — used by the sensitivity matrices to
  // recompute variants that reproduce the base case.
  const shownEngineOpts = strategy === 'ltr'
    ? assumptions
    : { ...assumptions, strategy: 'str', furnishingCost: Number(furnishingCost) || 0, str: strParams };

  // "Ask about this deal" — grounded context built straight from the same computed
  // analysis objects the report renders (never recomputed by the AI), plus a version
  // string so prior answers can be flagged stale the moment an assumption changes.
  const askContext = useMemo(
    () => buildAnalysisContext({ project, unit, analysis, analysisStr, strategy, capBands, rentBands, ils: effIls, rateMode }),
    [project, unit, analysis, analysisStr, strategy, capBands, rentBands, effIls, rateMode]
  );
  const askContextVersion = useMemo(() => JSON.stringify(askContext), [askContext]);

  // Compare mode: each unit uses ITS OWN saved assumptions by default (compareShared
  // off) — a shared band panel is an explicit, opt-in override, not the default.
  // Always compute BOTH the LTR and STR analysis per unit (same pattern as Single mode's
  // analysis/analysisStr) so switching the compare strategy is instant, with no recompute
  // lag, and "compare both" can show both side by side. STR params are always the unit's
  // OWN saved ADR/occupancy/expenses/furnishing — independent of the compareShared toggle,
  // which only affects the LTR projection bands/maturity/dld/admin (mirrors Bulk mode).
  const compareEntries = useMemo(() => {
    if (mode !== 'compare') return [];
    return compareSel.map(uid => {
      const found = allUnits.find(x => x.unit.id === uid);
      if (!found) return null;
      const { project: p, unit: u } = found;
      const ltrOpts = compareShared
        ? optsForUnit(p, u, compareCapBands, compareRentBands, rentOverrides[uid], compareMaturityYears)
        : optsFromUnit(p, u, rentOverrides[uid]);
      const fc = (u.furnishing_cost != null && u.furnishing_cost !== '')
        ? u.furnishing_cost : furnishingPreset(u.unit_type, u.is_fully_furnished, strConfig);
      const strOpts = strOptsFor(ltrOpts, u, fc);
      return {
        label: p.project_name, sub: `${u.unit_type} · ${aedStr(u.price_aed)}`, project: p, unit: u,
        analysis: computeAnalysis(p, u, ltrOpts),
        analysisStr: computeAnalysis(p, u, strOpts),
        hasStr: unitHasStr(u),
      };
    }).filter(Boolean);
  }, [mode, compareSel, allUnits, compareShared, compareCapBands, compareRentBands, rentOverrides, compareMaturityYears, strConfig]);

  // ---------- bulk deal resolution + aggregate analysis ----------
  const unitById = useMemo(() => {
    const m = {};
    for (const { project: p, unit: u } of allUnits) m[u.id] = { project: p, unit: u };
    return m;
  }, [allUnits]);

  // resolve raw bulk lines (unit_id refs) into engine-ready lines
  const bulkResolved = useMemo(() => {
    const strP = { adr: 0, occupancy: 0, ...numify(strExp) };
    return bulkLines.map(l => {
      const found = unitById[Number(l.unit_id)];
      if (!found) return null;
      const { project: p, unit: u } = found;
      const line = {
        project: p, unit: u,
        quantity: Number(l.quantity) || 1,
        priceOverride: l.price_override,
        rentOverride: l.rent_override,
      };
      if (bulkStrategy === 'str') {
        // per-unit STR params from the unit record (ADR/occupancy), fallback to page defaults
        line.str = { ...strP, adr: Number(u.str_adr) || 0, occupancy: Number(u.str_occupancy) || strP.occupancy };
        line.furnishingCost = (u.furnishing_cost != null && u.furnishing_cost !== '')
          ? Number(u.furnishing_cost) : furnishingPreset(u.unit_type, u.is_fully_furnished, strConfig);
      }
      return line;
    }).filter(Boolean);
  }, [bulkLines, unitById, bulkStrategy, strExp, strConfig]);

  const bulkAnalysis = useMemo(() => {
    if (mode !== 'bulk' || !bulkResolved.length) return null;
    const opts = { capBands: bulkCapBands, rentBands: bulkRentBands, maturityYears: bulkMaturityYears, strategy: bulkStrategy };
    const disc = bulkDiscountPct === '' ? null : Number(bulkDiscountPct);
    return computeBulkAnalysis(bulkResolved, opts, disc);
  }, [mode, bulkResolved, bulkCapBands, bulkRentBands, bulkMaturityYears, bulkStrategy, bulkDiscountPct]);

  function addBulkLine() {
    const first = allUnits[0];
    if (!first) { flash('Add a project with units first'); return; }
    setBulkLines(ls => [...ls, { unit_id: first.unit.id, quantity: 1, price_override: '', rent_override: '' }]);
  }
  function setBulkLine(i, k, v) {
    setBulkLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  }
  function removeBulkLine(i) { setBulkLines(ls => ls.filter((_, x) => x !== i)); }
  // "whichever is set last wins": setting a per-line price clears the bulk discount, and
  // vice-versa (so the effective prices are unambiguous).
  function setBulkLinePrice(i, v) {
    if (v !== '' && bulkDiscountPct !== '') setBulkDiscountPct('');
    setBulkLine(i, 'price_override', v);
  }
  function setBulkDiscount(v) {
    if (v !== '') setBulkLines(ls => ls.map(l => ({ ...l, price_override: '' })));
    setBulkDiscountPct(v);
  }

  function newBulkDeal() {
    setBulkDealId(null); setBulkName(''); setBulkLines([]); setBulkDiscountPct(''); setBulkStrategy('ltr');
    // seed a fresh deal's shared assumptions from the platform-default template
    const d = platformDefaults || DEFAULT_ASSUMPTIONS_TEMPLATE;
    setBulkCapBands(d.cap_bands || [8, 5, 3]); setBulkRentBands(d.rent_bands || [5, 3, 2]);
    setBulkMaturityYears(d.maturity_years || 10);
    setBulkResale(d.resale ? { enabled: !!d.resale.enabled, commissionPct: d.resale.commissionPct ?? 2, nocFee: d.resale.nocFee ?? 5250 } : RESALE_DEFAULTS);
  }
  function loadBulkDeal(id) {
    const d = bulkDeals.find(x => x.id === id);
    if (!d) { newBulkDeal(); return; }
    setBulkDealId(d.id); setBulkName(d.name || '');
    setBulkDiscountPct(d.bulk_discount_pct == null ? '' : String(d.bulk_discount_pct));
    setBulkLines((d.items || []).map(it => ({
      unit_id: it.unit_id, quantity: it.quantity || 1,
      price_override: it.price_override == null ? '' : String(it.price_override),
      rent_override: it.rent_override == null ? '' : String(it.rent_override),
    })));
    // this deal's OWN shared assumptions — independent of the units' and of any other deal
    setBulkStrategy(d.strategy || 'ltr');
    setBulkCapBands(d.cap_bands || [8, 5, 3]); setBulkRentBands(d.rent_bands || [5, 3, 2]);
    setBulkMaturityYears(d.maturity_years || 10);
    setBulkResale(d.resale ? { enabled: !!d.resale.enabled, commissionPct: d.resale.commissionPct ?? 2, nocFee: d.resale.nocFee ?? 5250 } : RESALE_DEFAULTS);
  }
  async function saveBulkDeal() {
    if (!bulkName.trim()) { flash('Name the deal first'); return; }
    if (!bulkLines.length) { flash('Add at least one unit line'); return; }
    setBulkSaving(true);
    const payload = {
      name: bulkName.trim(),
      bulk_discount_pct: bulkDiscountPct === '' ? null : Number(bulkDiscountPct),
      items: bulkLines.map(l => ({
        unit_id: Number(l.unit_id), quantity: Number(l.quantity) || 1,
        price_override: l.price_override === '' || l.price_override == null ? null : Number(l.price_override),
        rent_override: l.rent_override === '' || l.rent_override == null ? null : Number(l.rent_override),
      })),
      cap_bands: bulkCapBands, rent_bands: bulkRentBands, maturity_years: Number(bulkMaturityYears) || 10,
      strategy: bulkStrategy,
      resale: { enabled: !!bulkResale.enabled, commissionPct: Number(bulkResale.commissionPct) || 0, nocFee: Number(bulkResale.nocFee) || 0 },
    };
    try {
      const saved = bulkDealId
        ? (await axios.put(`${API}/analysis/bulk-deals/${bulkDealId}`, payload, { headers })).data
        : (await axios.post(`${API}/analysis/bulk-deals`, payload, { headers })).data;
      const list = (await axios.get(`${API}/analysis/bulk-deals`, { headers })).data;
      setBulkDeals(list); setBulkDealId(saved.id);
      flash(bulkDealId ? 'Deal saved ✓' : 'Deal created ✓');
    } catch (e) { flash('Save failed: ' + (e.response?.data?.detail || e.message)); }
    setBulkSaving(false);
  }
  function deleteBulkDeal() {
    if (!bulkDealId) { newBulkDeal(); return; }
    setConfirmState({
      title: 'Delete bulk deal', message: `Delete “${bulkName}”? This cannot be undone.`, confirmLabel: 'Delete deal',
      onConfirm: async () => {
        await axios.delete(`${API}/analysis/bulk-deals/${bulkDealId}`, { headers });
        setBulkDeals(bulkDeals.filter(d => d.id !== bulkDealId));
        newBulkDeal();
        flash('Deal deleted');
      },
    });
  }

  function flash(msg) { setToast(msg); setTimeout(() => setToast(''), 2500); }

  // ---------- shares ----------
  // Lists active links for whichever record is currently being shared — a unit
  // (single), an exact set of compared units (comparison), or a saved bulk deal.
  function loadShares() {
    if (mode === 'compare') {
      if (compareSel.length < 2) { setShares([]); return; }
      axios.get(`${API}/analysis/shares`, { headers, params: { comparison: [...compareSel].sort((a, b) => a - b).join(',') } })
        .then(r => setShares(r.data)).catch(() => {});
    } else if (mode === 'bulk') {
      if (!bulkDealId) { setShares([]); return; }   // unsaved deal has no stable id to list against
      axios.get(`${API}/analysis/shares`, { headers, params: { bulk_deal_id: bulkDealId } })
        .then(r => setShares(r.data)).catch(() => {});
    } else {
      if (!unit) { setShares([]); return; }
      axios.get(`${API}/analysis/shares`, { headers, params: { unit_id: unit.id } })
        .then(r => setShares(r.data)).catch(() => {});
    }
  }
  useEffect(() => { if (shareOpen) loadShares(); }, [shareOpen, mode, selUnitId, compareSel, bulkDealId]);  // eslint-disable-line

  async function createShare() {
    try {
      // Snapshot the AED→ILS rate used at share time so the client page + PDFs pin it
      // instead of following the live rate. {mode, rate_used}; footnote shows when manual.
      const rateSnap = { mode: rateMode, rate_used: Number(Number(effIls).toFixed(4)) };
      const resaleSnap = { enabled: !!resale.enabled, commissionPct: Number(resale.commissionPct) || 0, nocFee: Number(resale.nocFee) || 0 };
      const bulkResaleSnap = { enabled: !!bulkResale.enabled, commissionPct: Number(bulkResale.commissionPct) || 0, nocFee: Number(bulkResale.nocFee) || 0 };
      const payload = mode === 'compare'
        ? { comparison: compareSel, assumptions: { mode: 'comparison', lang: shareLang, perUnit: !compareShared, capBands: compareCapBands, rentBands: compareRentBands, rentOverrides, maturityYears: compareMaturityYears, strategy: compareStrategy, rate: rateSnap } }
        : mode === 'bulk'
        ? { bulk_deal_id: bulkDealId, assumptions: {
            mode: 'bulk', lang: shareLang, capBands: bulkCapBands, rentBands: bulkRentBands, maturityYears: bulkMaturityYears, strategy: bulkStrategy, resale: bulkResaleSnap, rate: rateSnap,
            deal: {
              name: bulkName || 'Package Purchase',
              bulk_discount_pct: bulkDiscountPct === '' ? null : Number(bulkDiscountPct),
              items: bulkLines.map(l => ({
                unit_id: Number(l.unit_id), quantity: Number(l.quantity) || 1,
                price_override: l.price_override === '' || l.price_override == null ? null : Number(l.price_override),
                rent_override: l.rent_override === '' || l.rent_override == null ? null : Number(l.rent_override),
              })),
            },
          } }
        : { unit_id: unit.id, allow_questions: shareAllowQuestions, assumptions: { mode: 'single', lang: shareLang, ...assumptions, strategy, furnishingCost: Number(furnishingCost) || 0, str: strParams, resale: resaleSnap, rate: rateSnap } };
      const { data } = await axios.post(`${API}/analysis/shares`, payload, { headers });
      try { await navigator.clipboard.writeText(data.url); } catch { /* ignore */ }
      flash('Share link copied to clipboard');
      loadShares();
    } catch (e) { flash('Could not create link'); }
  }
  async function revokeShare(id) {
    await axios.delete(`${API}/analysis/shares/${id}`, { headers });
    loadShares();
  }

  // ---------- pdf ----------
  // A4 desktop report — rasterises the on-screen (single) or comparison report.
  async function downloadA4() {
    const el = mode === 'compare' ? compareRef.current : mode === 'bulk' ? bulkRef.current : reportRef.current;
    if (!el) return;
    setPdfBusy(true);
    try {
      const name = mode === 'compare'
        ? pdfFilename('Comparison', `${compareEntries.length}-units`, 'A4')
        : mode === 'bulk'
        ? pdfFilename('Bulk-Deal', bulkName || `${bulkAnalysis?.units || 0}-units`, 'A4')
        : pdfFilename(project.project_name, unit.unit_type, 'A4');
      await exportElementToPdf(el, name);
      flash('PDF generated ✓');
    } catch (e) { flash('PDF export failed'); }
    setPdfBusy(false);
  }
  // Mobile report — rasterises the off-screen single-column phone layout.
  async function downloadMobile() {
    const el = mode === 'bulk' ? bulkMobileRef.current : mobileRef.current;
    if (!el) return;
    setPdfBusy(true);
    try {
      const name = mode === 'bulk'
        ? pdfFilename('Bulk-Deal', bulkName || `${bulkAnalysis?.units || 0}-units`, 'Mobile')
        : pdfFilename(project.project_name, unit.unit_type, 'Mobile');
      await exportMobileElementToPdf(el, name);
      flash('PDF generated ✓');
    } catch (e) { flash('PDF export failed'); }
    setPdfBusy(false);
  }

  // ---------- compare select ----------
  function toggleCompare(uid) {
    setCompareSel(sel => {
      if (sel.includes(uid)) return sel.filter(x => x !== uid);
      if (sel.length >= 3) { flash('Compare up to 3 units'); return sel; }
      return [...sel, uid];
    });
  }

  // ---------- form ----------
  function openAdd() { setEditId(null); setFormErrors({}); setForm(JSON.parse(JSON.stringify(EMPTY_FORM))); setShowForm(true); }
  function openEdit() {
    if (!project) return;
    setEditId(project.id); setFormErrors({});
    setForm({
      developer_name: project.developer_name || '', project_name: project.project_name || '',
      area: project.area || '', emirate: project.emirate || 'dubai',
      handover_date: project.handover_date ? project.handover_date.slice(0, 10) : '',
      description: project.description || '', admin_fees: project.admin_fees || 0, dld_enabled: project.dld_enabled,
      is_phpp: project.is_phpp || false, hero_image_url: project.hero_image_url || '',
      units: project.units.map(u => ({
        unit_type: u.unit_type, unit_number: u.unit_number ?? '', price_aed: u.price_aed, internal_sqft: u.internal_sqft, balcony_sqft: u.balcony_sqft,
        service_charge_rate: u.service_charge_rate, expected_rent_yearly_aed: u.expected_rent_yearly_aed,
        market_price_per_sqft: u.market_price_per_sqft ?? '', market_value_aed: u.market_value_aed ?? '',
        is_fully_furnished: u.is_fully_furnished || false,
        furnishing_cost: u.furnishing_cost ?? '', str_adr: u.str_adr ?? '', str_occupancy: u.str_occupancy ?? '',
        notes: u.notes || '',
      })),
      payment_plans: (project.payment_plans || []).map(m => ({
        milestone_label: m.milestone_label, percentage: m.percentage,
        due_date: m.due_date ? m.due_date.slice(0, 10) : '', months_from_booking: m.months_from_booking ?? '',
        is_handover: m.is_handover, is_post_handover: m.is_post_handover,
      })),
    });
    setShowForm(true);
  }
  const planTotal = form.payment_plans.reduce((s, m) => s + (Number(m.percentage) || 0), 0);
  const anyPhppMilestone = form.payment_plans.some(m => m.is_post_handover);
  function setUnit(i, k, v) { setForm({ ...form, units: form.units.map((u, idx) => idx === i ? { ...u, [k]: v } : u) }); }
  function setMile(i, k, v) {
    const pp = form.payment_plans.map((m, idx) => idx === i ? { ...m, [k]: v } : m);
    // a dated Handover milestone syncs the project handover_date field
    const patch = { payment_plans: pp };
    if (k === 'due_date' && pp[i].is_handover && v) patch.handover_date = v;
    setForm({ ...form, ...patch });
  }
  function moveMile(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= form.payment_plans.length) return;
    const arr = [...form.payment_plans];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setForm({ ...form, payment_plans: arr });
  }
  function duplicateMile(i) {
    const m = form.payment_plans[i];
    const copy = { ...m };
    if (copy.due_date) copy.due_date = addMonthsStr(copy.due_date, 1);
    else if (copy.months_from_booking !== '' && copy.months_from_booking != null) copy.months_from_booking = Number(copy.months_from_booking) + 1;
    const arr = [...form.payment_plans];
    arr.splice(i + 1, 0, copy);
    setForm({ ...form, payment_plans: arr });
  }
  function duplicateNMile(i) {
    const base = form.payment_plans[i];
    const nStr = window.prompt('Generate how many monthly instalments? (e.g. 24 for 1% × 24 months)', '24');
    const n = parseInt(nStr, 10);
    if (!n || n < 1 || n > 240) return;
    const prefix = (base.milestone_label || 'Post-handover').replace(/\s*\d+\s*\/\s*\d+\s*$/, '').trim() || 'Post-handover';
    const rows = [];
    for (let k = 0; k < n; k++) {
      const r = { ...base, milestone_label: `${prefix} ${k + 1}/${n}` };
      if (base.due_date) r.due_date = addMonthsStr(base.due_date, k);
      else if (base.months_from_booking !== '' && base.months_from_booking != null) r.months_from_booking = Number(base.months_from_booking) + k;
      rows.push(r);
    }
    const arr = [...form.payment_plans];
    arr.splice(i, 1, ...rows);   // expand the base row into N numbered rows
    setForm({ ...form, payment_plans: arr });
  }

  async function save() {
    const errs = {};
    if (!form.project_name.trim()) errs.project_name = 'Project name is required';
    if (Math.round(planTotal) !== 100) errs.plan = `Payment plan must total 100% (currently ${planTotal}%)`;
    setFormErrors(errs);
    if (Object.keys(errs).length) { flash('Please fix the highlighted fields'); return; }
    setSaving(true);
    const payload = {
      developer_name: form.developer_name || null, project_name: form.project_name, area: form.area || null,
      emirate: form.emirate, handover_date: form.handover_date || null, description: form.description || null,
      admin_fees: Number(form.admin_fees) || 0, dld_enabled: form.dld_enabled, is_phpp: form.is_phpp,
      hero_image_url: form.hero_image_url.trim() || null,
      units: form.units.map(u => {
        const internal = Number(u.internal_sqft) || 0;
        const mv = u.market_value_aed === '' || u.market_value_aed == null ? null : Number(u.market_value_aed);
        const mpsf = u.market_price_per_sqft === '' || u.market_price_per_sqft == null ? null : Number(u.market_price_per_sqft);
        // Derive the paired market field from internal sqft (whichever the user filled).
        let marketValue = mv, marketPsf = mpsf;
        if (marketValue == null && marketPsf != null && internal > 0) marketValue = marketPsf * internal;
        else if (marketPsf == null && marketValue != null && internal > 0) marketPsf = marketValue / internal;
        return {
          unit_type: u.unit_type, unit_number: (u.unit_number || '').trim() || null, price_aed: Number(u.price_aed) || 0, internal_sqft: internal,
          balcony_sqft: Number(u.balcony_sqft) || 0, service_charge_rate: Number(u.service_charge_rate) || 14,
          expected_rent_yearly_aed: Number(u.expected_rent_yearly_aed) || 0,
          market_price_per_sqft: marketPsf, market_value_aed: marketValue,
          is_fully_furnished: !!u.is_fully_furnished,
          furnishing_cost: (u.furnishing_cost === '' || u.furnishing_cost == null) ? null : Number(u.furnishing_cost),
          str_adr: (u.str_adr === '' || u.str_adr == null) ? null : Number(u.str_adr),
          str_occupancy: (u.str_occupancy === '' || u.str_occupancy == null) ? null : Number(u.str_occupancy),
          notes: u.notes || null, payment_plans: [],
        };
      }),
      payment_plans: form.payment_plans.map((m, idx) => ({
        milestone_label: m.milestone_label || `Milestone ${idx + 1}`, percentage: Number(m.percentage) || 0,
        due_date: m.due_date || null,
        months_from_booking: m.months_from_booking === '' ? null : Number(m.months_from_booking),
        is_handover: m.is_handover, is_post_handover: m.is_post_handover, sort_order: idx,
      })),
    };
    try {
      const saved = editId
        ? (await axios.put(`${API}/analysis/projects/${editId}`, payload, { headers })).data
        : (await axios.post(`${API}/analysis/projects`, payload, { headers })).data;
      const list = (await axios.get(`${API}/analysis/projects`, { headers })).data;
      setProjects(list); setSelProjectId(saved.id); setSelUnitId(saved.units[0]?.id ?? null);
      setShowForm(false); setFormErrors({});
      flash(editId ? 'Project saved ✓' : 'Project created ✓');
    } catch (e) { flash('Save failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  }
  function removeProject() {
    if (!project) return;
    setConfirmState({
      title: 'Delete project',
      message: `Delete “${project.project_name}” and all its units and payment milestones? This cannot be undone.`,
      confirmLabel: 'Delete project',
      onConfirm: async () => {
        await axios.delete(`${API}/analysis/projects/${project.id}`, { headers });
        const list = (await axios.get(`${API}/analysis/projects`, { headers })).data;
        setProjects(list); setSelProjectId(list[0]?.id ?? null); setSelUnitId(list[0]?.units[0]?.id ?? null);
        flash('Project deleted');
      },
    });
  }

  return (
    <div className="flex h-screen overflow-hidden bg-stone-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main ref={mainRef} onScroll={e => setScrolled(e.currentTarget.scrollTop > 260)} className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">

        {/* Sticky mini-header on scroll — keeps project/unit/price in view */}
        {mode === 'single' && project && unit && (
          <div className="sticky top-0 z-30 h-0">
            <div className={`-mx-4 md:-mx-8 px-4 md:px-8 py-2.5 bg-white/95 backdrop-blur border-b border-black/[0.06] shadow-premium flex items-center justify-between gap-3 transition-all duration-200 ${scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}`}>
              <div className="min-w-0 flex items-center gap-2 truncate">
                <span className="font-semibold text-gray-900 truncate">{project.project_name}</span>
                <span className="text-gray-300">·</span>
                <span className="text-sm text-gray-500 shrink-0">{unit.unit_type}</span>
                {((Number(unit.internal_sqft) || 0) + (Number(unit.balcony_sqft) || 0)) > 0 && (
                  <span className="hidden md:inline text-xs text-gray-400 shrink-0">· {areaStr((Number(unit.internal_sqft) || 0) + (Number(unit.balcony_sqft) || 0))}</span>
                )}
                {project.is_phpp && <span className="text-[9px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full shrink-0">PHPP</span>}
              </div>
              <div className="text-right shrink-0">
                <span className="font-semibold text-gray-900 tabular-nums">{aedStr(unit.price_aed)}</span>
                <span className="hidden sm:inline text-xs text-gray-400 ml-2 tabular-nums">{ilsStr(unit.price_aed * effIls)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Project Analysis</h1>
            <p className="text-gray-500 text-sm">Off-plan & ready property investment analysis — Dubai / RAK</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* EUR/USD show/hide — default hidden (AED + ₪ only); live rate shown as a caption when on */}
            <div className="flex items-center gap-1 mr-1">
              <button onClick={() => setShowEur(v => !v)} aria-pressed={showEur}
                title={showEur ? `Hide EUR (live 1 AED = €${Number(eur).toFixed(4)})` : 'Show EUR conversion'}
                className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold border transition ${showEur ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-white border-gray-200 text-gray-400 hover:text-gray-600'}`}>
                €{showEur && <span className="text-[9px] font-normal opacity-70 tabular-nums">{Number(eur).toFixed(4)}</span>}
              </button>
              <button onClick={() => setShowUsd(v => !v)} aria-pressed={showUsd}
                title={showUsd ? `Hide USD (live 1 AED = $${Number(usd).toFixed(4)})` : 'Show USD conversion'}
                className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold border transition ${showUsd ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-white border-gray-200 text-gray-400 hover:text-gray-600'}`}>
                ${showUsd && <span className="text-[9px] font-normal opacity-70 tabular-nums">{Number(usd).toFixed(4)}</span>}
              </button>
            </div>
            <div className="text-right mr-1">
              <div className="text-[11px] text-gray-400 flex items-center gap-1.5 justify-end">
                <span className={`inline-block w-2 h-2 rounded-full ${rateMode === 'manual' ? 'bg-amber-500' : (ilsLive ? 'bg-green-500' : 'bg-gray-300')}`}></span>
                {rateMode === 'manual' ? 'Fixed rate' : `${ilsLive ? 'Live' : 'Fallback'} rate`}
                {rateMode === 'manual' && <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide">manual rate</span>}
              </div>
              <div className="text-xs text-gray-600 font-medium">1 AED = ₪{Number(effIls).toFixed(4)}</div>
              {rateMode !== 'manual' && ilsLive && ilsUpdated && <div className="text-[10px] text-gray-400">updated {ilsUpdated.replace(/^\w+,\s*/, '').replace(/\s+\d{2}:\d{2}:\d{2}.*$/, '')}</div>}
            </div>
            {project && mode === 'single' && (
              <button onClick={openEdit} className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl text-sm font-medium">Edit</button>
            )}
            <button onClick={openDefaults} title="Platform-level template new units inherit from — doesn't change existing units" className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-500 px-3 py-2 rounded-xl text-sm font-medium">⚙ Defaults</button>
            <button onClick={openAdd} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium">+ Add Project</button>
          </div>
        </div>

        {loading ? (
          <div className="mt-2"><ReportSkeleton /></div>
        ) : projects.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center border border-gray-100 shadow-sm">
            <p className="text-4xl mb-3">🏗️</p>
            <p className="text-gray-700 font-medium mb-1">No projects yet</p>
            <p className="text-gray-400 text-sm mb-4">Add an off-plan or ready project to start analyzing.</p>
            <button onClick={openAdd} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium">+ Add Project</button>
          </div>
        ) : (
          <>
            {/* Mode toggle + actions */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1">
                <button onClick={() => setMode('single')} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${mode === 'single' ? 'bg-primary text-white' : 'text-gray-500'}`}>Single</button>
                <button onClick={() => setMode('compare')} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${mode === 'compare' ? 'bg-primary text-white' : 'text-gray-500'}`}>Compare</button>
                <button onClick={() => setMode('bulk')} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${mode === 'bulk' ? 'bg-primary text-white' : 'text-gray-500'}`}>Bulk Deal</button>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => setShareOpen(true)}
                  disabled={(mode === 'compare' && compareEntries.length < 2) || (mode === 'bulk' && !bulkAnalysis)}
                  className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl text-sm font-medium disabled:opacity-40">🔗 Share with client</button>
                {mode === 'compare' ? (
                  <button onClick={downloadA4} disabled={pdfBusy || compareEntries.length < 2}
                    className="bg-gray-900 hover:bg-black text-white px-3 py-2 rounded-xl text-sm font-medium disabled:opacity-40">{pdfBusy ? 'Generating…' : '⬇ Download PDF'}</button>
                ) : mode === 'bulk' ? (
                  <PdfDownloadButton onA4={downloadA4} onMobile={downloadMobile} busy={pdfBusy} disabled={!bulkAnalysis} />
                ) : (
                  <PdfDownloadButton onA4={downloadA4} onMobile={downloadMobile} busy={pdfBusy} />
                )}
              </div>
            </div>

            {/* ===================== SINGLE MODE ===================== */}
            {mode === 'single' && (
              <>
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-6 flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[220px]">
                    <label className="text-xs text-gray-500 block mb-1">Project</label>
                    <select value={selProjectId || ''}
                      onChange={e => { const id = Number(e.target.value); setSelProjectId(id); setSelUnitId(projects.find(p => p.id === id)?.units[0]?.id ?? null); }}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                      {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}{p.developer_name ? ` — ${p.developer_name}` : ''}{p.is_phpp ? ' • PHPP' : ''}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs text-gray-500 block mb-1">Unit Type</label>
                    <select value={selUnitId || ''} onChange={e => setSelUnitId(Number(e.target.value))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                      {project?.units.map(u => <option key={u.id} value={u.id}>{u.unit_type}{u.unit_number ? ` (${u.unit_number})` : ''} — {aedStr(u.price_aed)}</option>)}
                    </select>
                  </div>
                  <div className="text-sm text-gray-500">
                    {project?.area && <span className="mr-3">📍 {project.area}</span>}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${project?.emirate === 'rak' ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>{project?.emirate === 'rak' ? 'Ras Al Khaimah' : 'Dubai'}</span>
                    {project?.is_phpp && <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-600" title="Post-Handover Payment Plan — pay after receiving the keys">PHPP</span>}
                    {project?.handover_date && <span className="ml-3">🔑 Handover {monthYear(project.handover_date)}</span>}
                  </div>
                  {project && <button onClick={removeProject} className="text-xs text-red-400 hover:text-red-600 ml-auto">Delete project</button>}
                </div>

                {analysis && unit && (
                  <>
                    {/* rental strategy selector */}
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                      <span className="text-sm font-medium text-gray-600">Rental strategy</span>
                      <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1">
                        {[['ltr', 'Long-term'], ['str', 'Short-term (Airbnb)'], ['compare', 'Compare both']].map(([v, lbl]) => (
                          <button key={v} onClick={() => setStrategy(v)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${strategy === v ? (v === 'ltr' ? 'bg-primary text-white' : v === 'str' ? 'bg-sky-600 text-white' : 'bg-purple-600 text-white') : 'text-gray-500 hover:text-gray-700'}`}>{lbl}</button>
                        ))}
                      </div>
                      {strategy !== 'ltr' && (Number(adr) || 0) <= 0 && (
                        <span className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1">Enter an ADR below to see STR numbers →</span>
                      )}
                    </div>

                    {/* editable fees + assumptions — collapsed by default (the "knobs").
                        Scoped to THIS unit only: auto-saved (debounced) to its own record. */}
                    <details className="mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm group" open={strategy !== 'ltr'}>
                      <summary className="cursor-pointer list-none px-5 py-3.5 flex items-center justify-between text-sm font-semibold text-gray-700 select-none">
                        <span className="flex items-center gap-2">⚙️ Adjust assumptions & fees <span className="text-[11px] font-normal text-gray-400">— this unit only, {uaSaving ? 'saving…' : 'saved'}</span></span>
                        <span className="flex items-center gap-3">
                          <button type="button" onClick={e => { e.preventDefault(); resetUnitToDefaults(); }}
                            className="text-[11px] font-medium text-gray-400 hover:text-gray-700 underline underline-offset-2">Reset to defaults</button>
                          <span className="text-gray-300 text-xs group-open:rotate-180 transition-transform">▼</span>
                        </span>
                      </summary>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-5 pb-5 border-t border-gray-50 pt-4">
                      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                        <h3 className="font-semibold text-gray-800 mb-3">Fees</h3>
                        <div className="space-y-3 text-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-gray-700 flex items-center gap-1">DLD <input type="number" step="0.1" value={dldRate} onChange={e => setDldRate(e.target.value)} className="w-12 border border-gray-200 rounded px-1 py-0.5 text-xs text-right focus:outline-none focus:ring-2 focus:ring-green-500" />% {analysis.emirate === 'rak' ? '— at handover' : '— at 20% milestone'}</p>
                              <p className="text-xs text-gray-400">Developer sometimes pays this</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-gray-700 font-medium">{dldEnabled ? aedStr(analysis.dldAmount) : '—'}</span>
                              <button onClick={() => setDldEnabled(!dldEnabled)} className={`relative w-11 h-6 rounded-full transition ${dldEnabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition ${dldEnabled ? 'translate-x-5' : ''}`}></span>
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-gray-700">Admin fee <span className="text-xs text-gray-400">(at booking)</span></p>
                            <div className="flex items-center gap-1"><span className="text-xs text-gray-400">AED</span>
                              <input type="number" value={adminFees} onChange={e => setAdminFees(e.target.value)} className="w-28 border border-gray-200 rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-green-500" /></div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-gray-700">Service charge <span className="text-xs text-gray-400">(rate × sqft, from handover)</span></p>
                              <p className="text-xs text-gray-400">{scRate} AED/sqft · {unit.internal_sqft} internal + {unit.balcony_sqft} balcony (×0.25)</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <input type="number" value={scRate} onChange={e => setScRate(Number(e.target.value))} className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-green-500" />
                              <span className="text-gray-700 font-medium w-24 text-right">{aedStr(analysis.scYearly)}/yr</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                        <h3 className="font-semibold text-gray-800 mb-3">Projection Assumptions</h3>
                        <div className="space-y-3 text-sm">
                          <BandRow label="Capital appreciation %/yr" bands={capBands} setBands={setCapBands} />
                          <BandRow label="Rent appreciation %/yr" bands={rentBands} setBands={setRentBands} />
                          <div className="flex items-center justify-between">
                            <p className="text-gray-700">Starting rent (yearly)</p>
                            <div className="flex items-center gap-1"><span className="text-xs text-gray-400">AED</span>
                              <input type="number" value={startRent} onChange={e => setStartRent(e.target.value)} className="w-28 border border-gray-200 rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-green-500" /></div>
                          </div>
                          <MaturitySelect value={maturityYears} onChange={setMaturityYears} />
                          <div className="border-t border-gray-100 pt-3">
                            <ExchangeRateControl rateMode={rateMode} onLive={() => setRateMode('live')} onManual={enableManualRate}
                              manualIls={manualIls} setManualIls={setManualIls} liveIls={ils} ilsLive={ilsLive} outOfBounds={rateOutOfBounds} />
                          </div>
                          <p className="text-[11px] text-gray-400">Bands apply to years 1–3 / 4–6 / 7–10 from handover (years beyond 10 keep the 7–10 rate). Off-plan appreciates during construction at the year 1–3 rate.</p>
                        </div>
                      </div>
                      </div>
                      {strategy !== 'ltr' && (
                        <div className="px-5 pb-5">
                          <div className="bg-sky-50/60 rounded-2xl p-5 border border-sky-100">
                            <h3 className="font-semibold text-sky-800 mb-3 flex items-center gap-2">🏝️ Short-term rental (holiday home)</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                              <StrInput label="ADR (AED/night)" value={adr} onChange={setAdr} />
                              <StrInput label="Occupancy %" value={occupancy} onChange={setOccupancy} />
                              <StrInput label="Furnishing (AED)" value={furnishingCost} onChange={setFurnishingCost} hint="from unit; editable" />
                              <div>
                                <label className="text-xs text-gray-500 block mb-1">Gross revenue/yr</label>
                                <div className="px-2 py-1.5 rounded-lg bg-white border border-sky-100 text-sm font-semibold text-sky-700 tabular-nums">{aedStr((Number(adr) || 0) * 365 * (Number(occupancy) || 0) / 100)}</div>
                              </div>
                            </div>
                            <p className="text-xs font-medium text-gray-500 mb-2">Expense assumptions (editable, saved presets from config)</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <StrInput label="Management %" value={strExp.mgmtPct} onChange={v => setStrExp({ ...strExp, mgmtPct: v })} />
                              <StrInput label="DEWA (AED/mo)" value={strExp.dewaMonthly} onChange={v => setStrExp({ ...strExp, dewaMonthly: v })} />
                              <StrInput label="Gas (AED/mo)" value={strExp.gasMonthly} onChange={v => setStrExp({ ...strExp, gasMonthly: v })} />
                              <StrInput label="Internet (AED/mo)" value={strExp.internetMonthly} onChange={v => setStrExp({ ...strExp, internetMonthly: v })} />
                              <StrInput label="Chiller (AED/mo)" value={strExp.chillerMonthly} onChange={v => setStrExp({ ...strExp, chillerMonthly: v })} />
                              <StrInput label="DTCM license (AED/yr)" value={strExp.dtcmLicenseYearly} onChange={v => setStrExp({ ...strExp, dtcmLicenseYearly: v })} />
                              <StrInput label="Tourism dirham (AED/night)" value={strExp.tourismPerNight} onChange={v => setStrExp({ ...strExp, tourismPerNight: v })} />
                              <StrInput label="Furniture refresh %/yr" value={strExp.furnitureRefreshPct} onChange={v => setStrExp({ ...strExp, furnitureRefreshPct: v })} />
                              <StrInput label="Other/misc (AED/yr)" value={strExp.miscYearly} onChange={v => setStrExp({ ...strExp, miscYearly: v })} />
                            </div>
                          </div>
                        </div>
                      )}
                    </details>

                    {/* Lite / Advanced toggle — kept OUTSIDE the captured node so PDFs stay clean */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-medium text-gray-600">Detail</span>
                      <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1">
                        {[['lite', 'Lite'], ['advanced', 'Advanced']].map(([v, lbl]) => (
                          <button key={v} onClick={() => setView(v)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${view === v ? 'bg-dark text-white' : 'text-gray-500 hover:text-gray-700'}`}>{lbl}</button>
                        ))}
                      </div>
                      <span className="text-[11px] text-gray-400">{view === 'lite' ? 'client-ready essentials' : 'full tables, charts & sensitivity'}</span>
                    </div>

                    {/* report (captured for A4 PDF) — hero header lives inside AnalysisReport */}
                    <div ref={reportRef} className="bg-gray-50 p-1">
                      {strategy === 'compare' && analysisStr && analysis && (
                        <StrVsLtr ltr={analysis} str={analysisStr} ils={effIls} eur={effEur} usd={effUsd} />
                      )}
                      {shownAnalysis && (
                        <AnalysisReport project={project} unit={unit} analysis={shownAnalysis} ils={effIls} eur={effEur} usd={effUsd} assumptions={assumptions} engineOpts={shownEngineOpts} view={view} showSummary strategy={strategy === 'ltr' ? 'ltr' : 'str'} resale={resale} setResale={setResale}
                          altAnalysis={strategy === 'compare' ? analysis : null} exitYear={chatExitYear} onExitYearChange={setChatExitYear} />
                      )}
                      <ReportFooter fixedRate={rateMode === 'manual' ? effIls : null} />
                    </div>

                    {/* off-screen single-column layout, always mounted so its charts are
                        laid out and ready to rasterise the moment "PDF — Mobile" is clicked */}
                    {shownAnalysis && (
                      <div aria-hidden style={{ position: 'fixed', left: -10000, top: 0, width: 400, pointerEvents: 'none', zIndex: -1 }}>
                        <div ref={mobileRef} style={{ width: 400 }}>
                          <MobileReport project={project} unit={unit} analysis={shownAnalysis} ils={effIls} eur={effEur} usd={effUsd} assumptions={assumptions} strategy={strategy === 'ltr' ? 'ltr' : 'str'} resale={resale} fixedRate={rateMode === 'manual' ? effIls : null} />
                        </div>
                      </div>
                    )}

                    {/* "Ask about this deal" — deliberately OUTSIDE reportRef/mobileRef so it
                        never appears in an exported PDF. key=unit.id resets the chat when the
                        selected unit changes (a fresh deal, not a continuation). */}
                    {project && unit && (
                      <AnalysisAssistant key={unit.id}
                        context={askContext} contextVersion={askContextVersion}
                        askUrl={`${API}/analysis/ask`} authHeaders={headers}
                        matchInput={{ analysis, analysisStr, strategy, ils: effIls }}
                        onExitYearChange={setChatExitYear} />
                    )}
                  </>
                )}
              </>
            )}

            {/* ===================== COMPARE MODE ===================== */}
            {mode === 'compare' && (
              <>
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
                  <h3 className="font-semibold text-gray-800 mb-1">Select 2–3 units to compare</h3>
                  <p className="text-xs text-gray-400 mb-3">Units can be from different projects.</p>
                  <div className="flex flex-wrap gap-2">
                    {allUnits.map(({ project: p, unit: u }) => {
                      const on = compareSel.includes(u.id);
                      return (
                        <button key={u.id} onClick={() => toggleCompare(u.id)}
                          className={`px-3 py-2 rounded-xl text-sm border text-left ${on ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                          <span className="font-medium">{p.project_name}</span> · {u.unit_type}
                          {p.is_phpp && <span className="ml-1 text-[9px] bg-indigo-100 text-indigo-700 px-1 py-0.5 rounded-full" title="Post-Handover Payment Plan">PHPP</span>}
                          <span className="block text-xs text-gray-400">{aedStr(u.price_aed)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {compareEntries.length >= 2 && (
                  <>
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                      <span className="text-sm font-medium text-gray-600">Rental strategy</span>
                      <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1">
                        {[['ltr', 'Long-term'], ['str', 'Short-term (Airbnb)'], ['compare', 'Compare both']].map(([v, lbl]) => (
                          <button key={v} onClick={() => setCompareStrategy(v)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${compareStrategy === v ? (v === 'ltr' ? 'bg-primary text-white' : v === 'str' ? 'bg-sky-600 text-white' : 'bg-purple-600 text-white') : 'text-gray-500 hover:text-gray-700'}`}>{lbl}</button>
                        ))}
                      </div>
                      <span className="text-[11px] text-gray-400">Applies to the whole comparison — each unit still uses its own ADR/occupancy/STR expenses.</span>
                    </div>

                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <h3 className="font-semibold text-gray-800">Assumptions</h3>
                          <p className="text-xs text-gray-400">Each unit uses its own saved assumptions by default.</p>
                        </div>
                        <label className="flex items-center gap-2 text-xs text-gray-600 shrink-0">
                          <button type="button" onClick={() => setCompareShared(v => !v)}
                            className={`relative w-11 h-6 rounded-full transition shrink-0 ${compareShared ? 'bg-amber-500' : 'bg-gray-300'}`}>
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition ${compareShared ? 'translate-x-5' : ''}`}></span>
                          </button>
                          Apply one set to all
                        </label>
                      </div>
                      {compareShared && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4 mt-3 bg-amber-50/60 border border-amber-100 rounded-xl p-4">
                          <BandRow label="Capital appreciation %/yr" bands={compareCapBands} setBands={setCompareCapBands} />
                          <BandRow label="Rent appreciation %/yr" bands={compareRentBands} setBands={setCompareRentBands} />
                          <MaturitySelect value={compareMaturityYears} onChange={setCompareMaturityYears} />
                          <ExchangeRateControl rateMode={rateMode} onLive={() => setRateMode('live')} onManual={enableManualRate}
                            manualIls={manualIls} setManualIls={setManualIls} liveIls={ils} ilsLive={ilsLive} outOfBounds={rateOutOfBounds} />
                        </div>
                      )}
                      <p className="text-xs text-gray-400 mb-2 mt-3">Per-unit expected rent override (yearly AED):</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {compareEntries.map(e => (
                          <div key={e.unit.id}>
                            <label className="text-xs text-gray-500 block mb-1">{e.label} · {e.unit.unit_type}</label>
                            <input type="number" placeholder={String(e.unit.expected_rent_yearly_aed || 0)}
                              value={rentOverrides[e.unit.id] ?? ''} onChange={ev => setRentOverrides({ ...rentOverrides, [e.unit.id]: ev.target.value })}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div ref={compareRef} className="bg-gray-50 p-1">
                      <div className="mb-4 px-1">
                        <h2 className="text-xl font-bold text-gray-900">Unit Comparison</h2>
                        <p className="text-sm text-gray-500">{compareEntries.map(e => `${e.label} ${e.unit.unit_type}`).join('  vs  ')}</p>
                      </div>
                      <ComparisonReport entries={compareEntries} ils={effIls} eur={effEur} usd={effUsd} strategy={compareStrategy} />
                    </div>
                  </>
                )}
                {compareEntries.length < 2 && <p className="text-gray-400 text-sm">Pick at least 2 units above to see the comparison.</p>}
              </>
            )}

            {/* ===================== BULK DEAL MODE ===================== */}
            {mode === 'bulk' && (
              <>
                {/* deal builder */}
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
                  <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                    <div className="flex-1 min-w-[220px]">
                      <label className="text-xs text-gray-500 block mb-1">Deal name</label>
                      <input value={bulkName} onChange={e => setBulkName(e.target.value)} placeholder="e.g. Weston — 5-unit package"
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div className="min-w-[180px]">
                      <label className="text-xs text-gray-500 block mb-1">Saved deals</label>
                      <select value={bulkDealId || ''} onChange={e => e.target.value ? loadBulkDeal(Number(e.target.value)) : newBulkDeal()}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                        <option value="">— New deal —</option>
                        {bulkDeals.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={saveBulkDeal} disabled={bulkSaving} className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-sm font-medium">{bulkSaving ? 'Saving…' : (bulkDealId ? 'Save' : 'Create')}</button>
                      <button onClick={newBulkDeal} className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 px-3 py-2 rounded-xl text-sm">New</button>
                      {bulkDealId && <button onClick={deleteBulkDeal} className="text-xs text-red-400 hover:text-red-600">Delete</button>}
                    </div>
                  </div>

                  {/* unit lines */}
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-800 text-sm">Units in this deal</h3>
                      <button onClick={addBulkLine} className="text-xs text-green-600 font-medium">+ Add unit line</button>
                    </div>
                    {bulkLines.length === 0 ? (
                      <p className="text-sm text-gray-400 py-2">No units yet — add a line to start building the package.</p>
                    ) : (
                      <div className="space-y-2">
                        <div className="hidden md:grid grid-cols-12 gap-2 text-[11px] text-gray-400 px-1">
                          <span className="col-span-5">Unit</span><span className="col-span-1 text-right">Qty</span>
                          <span className="col-span-3 text-right">Bulk price / unit</span><span className="col-span-2 text-right">Rent/yr / unit</span><span className="col-span-1"></span>
                        </div>
                        {bulkLines.map((l, i) => {
                          const found = unitById[Number(l.unit_id)];
                          const list = found ? found.unit.price_aed : 0;
                          return (
                            <div key={i} className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center bg-gray-50 rounded-xl p-2">
                              <div className="col-span-2 md:col-span-5">
                                <select value={l.unit_id} onChange={e => setBulkLine(i, 'unit_id', Number(e.target.value))}
                                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500">
                                  {allUnits.map(({ project: p, unit: u }) => (
                                    <option key={u.id} value={u.id}>{p.project_name} · {u.unit_type}{u.unit_number ? ` (${u.unit_number})` : ''} — {aedStr(u.price_aed)}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="md:col-span-1"><input type="number" min="1" value={l.quantity} onChange={e => setBulkLine(i, 'quantity', e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-right bg-white focus:outline-none focus:ring-2 focus:ring-green-500" /></div>
                              <div className="md:col-span-3"><input type="number" value={l.price_override} placeholder={String(list || 'list')}
                                onChange={e => setBulkLinePrice(i, e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-right bg-white focus:outline-none focus:ring-2 focus:ring-green-500" /></div>
                              <div className="md:col-span-2"><input type="number" value={l.rent_override} placeholder={String(found?.unit.expected_rent_yearly_aed || 'default')}
                                onChange={e => setBulkLine(i, 'rent_override', e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-right bg-white focus:outline-none focus:ring-2 focus:ring-green-500" /></div>
                              <div className="md:col-span-1 text-right"><button onClick={() => removeBulkLine(i)} className="text-red-400 hover:text-red-600 text-sm px-1">✕</button></div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* bulk discount + strategy */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="bg-gray-50 rounded-xl p-3">
                        <label className="text-xs text-gray-500 block mb-1">Bulk discount % <span className="text-gray-300">(applied to all list prices)</span></label>
                        <div className="flex items-center gap-2">
                          <input type="number" step="0.1" value={bulkDiscountPct} placeholder="e.g. 8"
                            onChange={e => setBulkDiscount(e.target.value)}
                            className="w-28 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-right bg-white focus:outline-none focus:ring-2 focus:ring-green-500" />
                          <span className="text-xs text-gray-400">%</span>
                          <span className="text-[11px] text-gray-400">Setting this clears per-line prices (last wins).</span>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3">
                        <label className="text-xs text-gray-500 block mb-1">Rental strategy (whole package)</label>
                        <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1">
                          {[['ltr', 'Long-term'], ['str', 'Short-term (Airbnb)']].map(([v, lbl]) => (
                            <button key={v} onClick={() => setBulkStrategy(v)}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${bulkStrategy === v ? (v === 'ltr' ? 'bg-primary text-white' : 'bg-sky-600 text-white') : 'text-gray-500 hover:text-gray-700'}`}>{lbl}</button>
                          ))}
                        </div>
                        {bulkStrategy === 'str' && <p className="text-[11px] text-sky-600 mt-1.5">STR uses each unit's saved ADR / occupancy / furnishing.</p>}
                      </div>
                    </div>
                  </div>
                </div>

                {bulkAnalysis ? (
                  <>
                    {/* deal-level shared assumptions — saved on this bulk deal, independent of
                        every unit's own saved assumptions and of any other deal */}
                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
                      <h3 className="font-semibold text-gray-800 mb-1">Deal Assumptions</h3>
                      <p className="text-xs text-gray-400 mb-3">Shared across this package (saved with the deal) — each line still uses its own unit's DLD/admin/service-charge.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <BandRow label="Capital appreciation %/yr" bands={bulkCapBands} setBands={setBulkCapBands} />
                        <BandRow label="Rent appreciation %/yr" bands={bulkRentBands} setBands={setBulkRentBands} />
                        <MaturitySelect value={bulkMaturityYears} onChange={setBulkMaturityYears} />
                        <ExchangeRateControl rateMode={rateMode} onLive={() => setRateMode('live')} onManual={enableManualRate}
                          manualIls={manualIls} setManualIls={setManualIls} liveIls={ils} ilsLive={ilsLive} outOfBounds={rateOutOfBounds} />
                      </div>
                    </div>

                    {/* Lite / Advanced toggle */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-medium text-gray-600">Detail</span>
                      <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1">
                        {[['lite', 'Lite'], ['advanced', 'Advanced']].map(([v, lbl]) => (
                          <button key={v} onClick={() => setView(v)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${view === v ? 'bg-dark text-white' : 'text-gray-500 hover:text-gray-700'}`}>{lbl}</button>
                        ))}
                      </div>
                    </div>

                    <div ref={bulkRef} className="bg-gray-50 p-1">
                      <BulkReport bulk={bulkAnalysis} ils={effIls} eur={effEur} usd={effUsd} dealName={bulkName} resale={bulkResale} setResale={setBulkResale} view={view} />
                      <ReportFooter fixedRate={rateMode === 'manual' ? effIls : null} />
                    </div>

                    {/* off-screen narrow copy for the Mobile PDF */}
                    <div aria-hidden style={{ position: 'fixed', left: -10000, top: 0, width: 400, pointerEvents: 'none', zIndex: -1 }}>
                      <div ref={bulkMobileRef} style={{ width: 400 }}>
                        <BulkReport bulk={bulkAnalysis} ils={effIls} eur={effEur} usd={effUsd} dealName={bulkName} resale={bulkResale} view="lite" presentation />
                        <ReportFooter fixedRate={rateMode === 'manual' ? effIls : null} />
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-400 text-sm">Add at least one unit line above to see the package analysis.</p>
                )}
              </>
            )}
          </>
        )}
      </main>

      {/* toast */}
      {toast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] bg-gray-900 text-white text-sm px-4 py-2 rounded-xl shadow-lg animate-[fadeIn_.15s_ease-out]">{toast}</div>}

      {/* confirmation dialog (destructive actions) */}
      {confirmState && (
        <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={() => setConfirmState(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-lg shrink-0">⚠️</div>
              <div>
                <h3 className="font-bold text-gray-900">{confirmState.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{confirmState.message}</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmState(null)} className="px-4 py-2 rounded-xl text-sm text-gray-600 bg-gray-100 hover:bg-gray-200">Cancel</button>
              <button onClick={async () => { const fn = confirmState.onConfirm; setConfirmState(null); try { await fn(); } catch { flash('Action failed'); } }}
                className="px-4 py-2 rounded-xl text-sm text-white bg-red-600 hover:bg-red-700 font-medium">{confirmState.confirmLabel || 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Platform Defaults (template new units inherit from) ===== */}
      {defaultsOpen && defaultsForm && (
        <div className="fixed inset-0 z-[70] bg-black bg-opacity-40 flex justify-end" onClick={() => setDefaultsOpen(false)}>
          <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Platform Defaults</h2>
              <button onClick={() => setDefaultsOpen(false)} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-sm text-gray-500">The starting template copied into every <em>new</em> unit's own assumptions. Changing this does not touch any existing unit — use each unit's own "Reset to defaults" for that.</p>
              <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 space-y-3 text-sm">
                <BandRow label="Capital appreciation %/yr" bands={defaultsForm.cap_bands} setBands={b => setDefaultsForm({ ...defaultsForm, cap_bands: b })} />
                <BandRow label="Rent appreciation %/yr" bands={defaultsForm.rent_bands} setBands={b => setDefaultsForm({ ...defaultsForm, rent_bands: b })} />
                <MaturitySelect value={defaultsForm.maturity_years} onChange={v => setDefaultsForm({ ...defaultsForm, maturity_years: v })} />
                <div className="flex items-center justify-between">
                  <p className="text-gray-700">DLD rate</p>
                  <div className="flex items-center gap-1">
                    <input type="number" step="0.1" value={defaultsForm.dld_rate} onChange={e => setDefaultsForm({ ...defaultsForm, dld_rate: e.target.value })} className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm text-right" />
                    <span className="text-xs text-gray-400">%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-gray-700">Admin fee (AED)</p>
                  <input type="number" value={defaultsForm.admin_fees} onChange={e => setDefaultsForm({ ...defaultsForm, admin_fees: e.target.value })} className="w-28 border border-gray-200 rounded-lg px-2 py-1 text-sm text-right" />
                </div>
              </div>
              <div className="bg-sky-50/60 rounded-2xl p-4 border border-sky-100">
                <p className="text-xs font-medium text-gray-500 mb-2">STR expense defaults</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <StrInput label="Management %" value={defaultsForm.str_expenses.mgmtPct} onChange={v => setDefaultsForm({ ...defaultsForm, str_expenses: { ...defaultsForm.str_expenses, mgmtPct: v } })} />
                  <StrInput label="DEWA (AED/mo)" value={defaultsForm.str_expenses.dewaMonthly} onChange={v => setDefaultsForm({ ...defaultsForm, str_expenses: { ...defaultsForm.str_expenses, dewaMonthly: v } })} />
                  <StrInput label="Gas (AED/mo)" value={defaultsForm.str_expenses.gasMonthly} onChange={v => setDefaultsForm({ ...defaultsForm, str_expenses: { ...defaultsForm.str_expenses, gasMonthly: v } })} />
                  <StrInput label="Internet (AED/mo)" value={defaultsForm.str_expenses.internetMonthly} onChange={v => setDefaultsForm({ ...defaultsForm, str_expenses: { ...defaultsForm.str_expenses, internetMonthly: v } })} />
                  <StrInput label="Chiller (AED/mo)" value={defaultsForm.str_expenses.chillerMonthly} onChange={v => setDefaultsForm({ ...defaultsForm, str_expenses: { ...defaultsForm.str_expenses, chillerMonthly: v } })} />
                  <StrInput label="DTCM (AED/yr)" value={defaultsForm.str_expenses.dtcmLicenseYearly} onChange={v => setDefaultsForm({ ...defaultsForm, str_expenses: { ...defaultsForm.str_expenses, dtcmLicenseYearly: v } })} />
                  <StrInput label="Tourism (AED/night)" value={defaultsForm.str_expenses.tourismPerNight} onChange={v => setDefaultsForm({ ...defaultsForm, str_expenses: { ...defaultsForm.str_expenses, tourismPerNight: v } })} />
                  <StrInput label="Furniture refresh %/yr" value={defaultsForm.str_expenses.furnitureRefreshPct} onChange={v => setDefaultsForm({ ...defaultsForm, str_expenses: { ...defaultsForm.str_expenses, furnitureRefreshPct: v } })} />
                </div>
              </div>
              <button onClick={saveDefaults} disabled={defaultsSaving} className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-medium">{defaultsSaving ? 'Saving…' : 'Save defaults'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Share panel ===== */}
      {shareOpen && (
        <div className="fixed inset-0 z-[70] bg-black bg-opacity-40 flex justify-end" onClick={() => setShareOpen(false)}>
          <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Share with Client</h2>
              <button onClick={() => setShareOpen(false)} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-sm text-gray-500">
                {mode === 'compare'
                  ? `Generate a read-only link for this ${compareEntries.length}-unit comparison. The client sees it without logging in; assumptions are snapshotted now.`
                  : mode === 'bulk'
                  ? `Generate a read-only link for this ${bulkAnalysis?.units || 0}-unit bulk deal. The client sees the package analysis without logging in; the composition and assumptions are snapshotted now.`
                  : 'Generate a read-only link for this unit. The client sees a clean, presentation-ready page without logging in. The assumptions you see now are snapshotted into the link.'}
              </p>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-gray-500">Default language for the link</span>
                <div className="inline-flex bg-gray-100 border border-gray-200 rounded-lg p-0.5 text-xs">
                  {[['en', 'EN'], ['he', 'עברית']].map(([v, lbl]) => (
                    <button key={v} onClick={() => setShareLang(v)}
                      className={`px-2.5 py-1 rounded-md font-semibold transition ${shareLang === v ? 'bg-white shadow-sm text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}>{lbl}</button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-gray-400 -mt-2">The client can still switch language on the page; this only sets the initial view.</p>
              {mode === 'single' && (
                <div className="flex items-center justify-between gap-3 bg-sky-50/60 border border-sky-100 rounded-xl px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-sky-800">💬 Allow client to ask questions</p>
                    <p className="text-[11px] text-sky-500/80">Adds "Ask about this deal" to the link, grounded in this snapshot — off by default.</p>
                  </div>
                  <button type="button" onClick={() => setShareAllowQuestions(v => !v)} aria-pressed={shareAllowQuestions}
                    className={`relative w-11 h-6 rounded-full transition shrink-0 ${shareAllowQuestions ? 'bg-sky-500' : 'bg-gray-300'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition ${shareAllowQuestions ? 'translate-x-5' : ''}`}></span>
                  </button>
                </div>
              )}
              <button onClick={createShare} className="w-full bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-xl text-sm font-medium">🔗 Create & copy link</button>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  {mode === 'compare' ? 'Active links for this comparison' : mode === 'bulk' ? 'Active links for this deal' : 'Active links for this unit'}
                </h3>
                {mode === 'bulk' && !bulkDealId ? (
                  <p className="text-sm text-gray-400">Save this deal first to track its share links.</p>
                ) : shares.length === 0 ? (
                  <p className="text-sm text-gray-400">No active links yet.</p>
                ) : (
                  <div className="space-y-2">
                    {shares.map(s => (
                      <div key={s.id} className="border border-gray-100 rounded-xl p-3 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <code className="text-xs text-gray-500 truncate flex-1">/share/analysis/{s.share_token}</code>
                          <button onClick={() => { navigator.clipboard?.writeText(s.url); flash('Copied'); }} className="text-xs text-green-600 font-medium shrink-0">Copy</button>
                          <button onClick={() => revokeShare(s.id)} className="text-xs text-red-400 hover:text-red-600 shrink-0">Revoke</button>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">👁 {s.view_count} views · created {new Date(s.created_at).toLocaleDateString('en-GB')}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Add / Edit form ===== */}
      {showForm && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-40 flex justify-end" onClick={() => setShowForm(false)}>
          <div className="bg-gray-50 w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-lg font-bold text-gray-900">{editId ? 'Edit Project' : 'Add Project'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
            </div>
            <div className="p-6 space-y-6">
              <div className="bg-white rounded-2xl p-5 border border-gray-100 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Project name *">
                    <input value={form.project_name} onChange={e => { setForm({ ...form, project_name: e.target.value }); if (formErrors.project_name) setFormErrors({ ...formErrors, project_name: undefined }); }} className={`inp ${formErrors.project_name ? 'border-red-400 ring-1 ring-red-300' : ''}`} />
                    {formErrors.project_name && <span className="text-[11px] text-red-500 mt-1 block">{formErrors.project_name}</span>}
                  </Field>
                  <Field label="Developer"><input value={form.developer_name} onChange={e => setForm({ ...form, developer_name: e.target.value })} className="inp" /></Field>
                  <Field label="Area"><input value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} placeholder="JVC, Dubai Marina…" className="inp" /></Field>
                  <Field label="Emirate">
                    <select value={form.emirate} onChange={e => setForm({ ...form, emirate: e.target.value })} className="inp"><option value="dubai">Dubai</option><option value="rak">Ras Al Khaimah</option></select>
                  </Field>
                  <Field label="Handover date"><input type="date" value={form.handover_date} onChange={e => setForm({ ...form, handover_date: e.target.value })} className="inp" /></Field>
                  <Field label="Admin fees (AED)"><input type="number" value={form.admin_fees} onChange={e => setForm({ ...form, admin_fees: e.target.value })} className="inp" /></Field>
                </div>
                <Field label="Hero image URL (cover photo shown on the report + share page — leave blank for a brand gradient)">
                  <input value={form.hero_image_url} onChange={e => setForm({ ...form, hero_image_url: e.target.value })} placeholder="https://…" className="inp" />
                  {form.hero_image_url && (
                    <div className="mt-2 h-24 rounded-xl overflow-hidden bg-gray-100 bg-cover bg-center border border-gray-200" style={{ backgroundImage: `url(${form.hero_image_url})` }} />
                  )}
                </Field>
                <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.dld_enabled} onChange={e => setForm({ ...form, dld_enabled: e.target.checked })} />DLD 4% applies (uncheck if the developer pays it)</label>
                <div className="flex items-center justify-between bg-indigo-50/60 border border-indigo-100 rounded-xl px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-indigo-800">PHPP project</p>
                    <p className="text-xs text-indigo-500/80">Post-Handover Payment Plan — buyer keeps paying after receiving the keys</p>
                  </div>
                  <button type="button" onClick={() => setForm({ ...form, is_phpp: !form.is_phpp })}
                    className={`relative w-11 h-6 rounded-full transition shrink-0 ${form.is_phpp ? 'bg-indigo-500' : 'bg-gray-300'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition ${form.is_phpp ? 'translate-x-5' : ''}`}></span>
                  </button>
                </div>
                <Field label="Description"><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="inp" /></Field>
              </div>

              <div className="bg-white rounded-2xl p-5 border border-gray-100">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-800 text-sm">Units</h3>
                  <button onClick={() => setForm({ ...form, units: [...form.units, { ...EMPTY_UNIT }] })} className="text-xs text-green-600 font-medium">+ Add unit</button>
                </div>
                <div className="space-y-4">
                  {form.units.map((u, i) => (
                    <div key={i} className="border border-gray-100 rounded-xl p-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-gray-400">Unit {i + 1}</span>
                        {form.units.length > 1 && <button onClick={() => setForm({ ...form, units: form.units.filter((_, x) => x !== i) })} className="text-xs text-red-400">Remove</button>}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        <Field label="Type"><select value={u.unit_type} onChange={e => setUnit(i, 'unit_type', e.target.value)} className="inp">{UNIT_TYPES.map(t => <option key={t}>{t}</option>)}</select></Field>
                        <Field label="Unit #"><input type="text" value={u.unit_number || ''} onChange={e => setUnit(i, 'unit_number', e.target.value)} placeholder="e.g. T3-325" className="inp" /></Field>
                        <Field label="Price (AED)"><input type="number" value={u.price_aed} onChange={e => setUnit(i, 'price_aed', e.target.value)} className="inp" /></Field>
                        <Field label="Rent/yr (AED)"><input type="number" value={u.expected_rent_yearly_aed} onChange={e => setUnit(i, 'expected_rent_yearly_aed', e.target.value)} className="inp" /></Field>
                        <Field label="Internal sqft"><input type="number" value={u.internal_sqft} onChange={e => setUnit(i, 'internal_sqft', e.target.value)} className="inp" /></Field>
                        <Field label="Balcony sqft"><input type="number" value={u.balcony_sqft} onChange={e => setUnit(i, 'balcony_sqft', e.target.value)} className="inp" /></Field>
                        <Field label="SC rate (AED/sqft)"><input type="number" value={u.service_charge_rate} onChange={e => setUnit(i, 'service_charge_rate', e.target.value)} className="inp" /></Field>
                      </div>
                      {/* Discount-to-market (optional): fill EITHER one, the other is derived from internal sqft */}
                      <div className="mt-2 pt-2 border-t border-dashed border-gray-100">
                        <p className="text-[11px] text-gray-400 mb-1.5">Market comparison (optional) — fill either one; the other is derived from internal sqft.</p>
                        <div className="grid grid-cols-2 gap-2">
                          <Field label="Market price/sqft (AED)"><input type="number" value={u.market_price_per_sqft} placeholder="e.g. 1600"
                            onChange={e => setUnit(i, 'market_price_per_sqft', e.target.value)} className="inp" /></Field>
                          <Field label="Market value (AED)"><input type="number" value={u.market_value_aed} placeholder="e.g. 800000"
                            onChange={e => setUnit(i, 'market_value_aed', e.target.value)} className="inp" /></Field>
                        </div>
                        {(() => {
                          const internal = Number(u.internal_sqft) || 0;
                          const price = Number(u.price_aed) || 0;
                          const mv = u.market_value_aed !== '' && u.market_value_aed != null ? Number(u.market_value_aed)
                            : (u.market_price_per_sqft !== '' && u.market_price_per_sqft != null && internal > 0 ? Number(u.market_price_per_sqft) * internal : 0);
                          if (!(mv > 0 && price > 0)) return null;
                          const disc = (mv - price) / mv * 100;
                          return <p className="text-[11px] text-teal-600 mt-1">Derived market value {aedStr(mv)} · {disc >= 0 ? `${disc.toFixed(1)}% below market` : `${Math.abs(disc).toFixed(1)}% above market`} · instant equity {aedStr(mv - price)}</p>;
                        })()}
                      </div>
                      {/* Short-term rental (holiday home) fields */}
                      <div className="mt-2 pt-2 border-t border-dashed border-gray-100">
                        <div className="flex items-center justify-between bg-sky-50/60 border border-sky-100 rounded-lg px-3 py-2 mb-2">
                          <div>
                            <p className="text-sm font-medium text-sky-800">🏝️ Fully furnished by developer</p>
                            <p className="text-[11px] text-sky-500/80">Toggling swaps the furnishing-cost preset</p>
                          </div>
                          <button type="button"
                            onClick={() => { const nf = !u.is_fully_furnished; setForm(f => ({ ...f, units: f.units.map((x, idx) => idx === i ? { ...x, is_fully_furnished: nf, furnishing_cost: furnishingPreset(x.unit_type, nf, strConfig) } : x) })); }}
                            className={`relative w-11 h-6 rounded-full transition shrink-0 ${u.is_fully_furnished ? 'bg-sky-500' : 'bg-gray-300'}`}>
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition ${u.is_fully_furnished ? 'translate-x-5' : ''}`}></span>
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <Field label="Furnishing cost (AED)"><input type="number" value={u.furnishing_cost}
                            placeholder={String(furnishingPreset(u.unit_type, u.is_fully_furnished, strConfig))}
                            onChange={e => setUnit(i, 'furnishing_cost', e.target.value)} className="inp" /></Field>
                          <Field label="STR ADR (AED/night)"><input type="number" value={u.str_adr} placeholder="e.g. 450"
                            onChange={e => setUnit(i, 'str_adr', e.target.value)} className="inp" /></Field>
                          <Field label="STR occupancy %"><input type="number" value={u.str_occupancy} placeholder="75"
                            onChange={e => setUnit(i, 'str_occupancy', e.target.value)} className="inp" /></Field>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">
                          Preset for {u.unit_type} {u.is_fully_furnished ? '(furnished)' : '(unfurnished)'}: {aedStr(furnishingPreset(u.unit_type, u.is_fully_furnished, strConfig))}
                          {' · '}<button type="button" className="underline text-sky-600" onClick={() => setUnit(i, 'furnishing_cost', furnishingPreset(u.unit_type, u.is_fully_furnished, strConfig))}>use preset</button>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 border border-gray-100">
                <div className="flex justify-between items-center mb-1">
                  <h3 className="font-semibold text-gray-800 text-sm">Payment Plan (project-level)</h3>
                  <button onClick={() => setForm({ ...form, payment_plans: [...form.payment_plans, { ...EMPTY_MILESTONE }] })} className="text-xs text-green-600 font-medium">+ Add milestone</button>
                </div>
                <div className="mb-3">
                  {Math.round(planTotal) === 100 ? (
                    <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-100 text-green-600 text-[10px]">✓</span>
                      Payment plan totals 100%
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600 font-medium">Running total: {planTotal}% · {(100 - planTotal).toFixed(0)}% remaining to reach 100%</p>
                  )}
                  <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${Math.round(planTotal) === 100 ? 'bg-green-400' : 'bg-amber-400'}`} style={{ width: `${Math.min(100, Math.max(0, planTotal))}%` }} />
                  </div>
                  {formErrors.plan && <span className="text-[11px] text-red-500 mt-1 block">{formErrors.plan}</span>}
                </div>
                <div className="space-y-2">
                  {form.payment_plans.map((m, i) => (
                    <div key={i} className="border border-gray-100 rounded-xl p-2.5 space-y-2">
                      {/* line 1: label + % */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-gray-400 w-6 shrink-0">#{i + 1}</span>
                        <input value={m.milestone_label} onChange={e => setMile(i, 'milestone_label', e.target.value)} placeholder="Milestone label" className="inp flex-1 min-w-[140px]" />
                        <div className="flex items-center gap-1 shrink-0">
                          <input type="number" value={m.percentage} onChange={e => setMile(i, 'percentage', e.target.value)} placeholder="%" className="inp w-16 text-right" />
                          <span className="text-xs text-gray-400">%</span>
                        </div>
                      </div>
                      {/* line 2: date, months fallback, flags */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <label className="flex items-center gap-1 text-xs text-gray-500">
                          <span className="shrink-0">Due date</span>
                          <input type="date" value={m.due_date || ''} onChange={e => setMile(i, 'due_date', e.target.value)} className="inp w-40" />
                        </label>
                        <label className="flex items-center gap-1 text-xs text-gray-500" title="Fallback used only when no date is set">
                          <span className="shrink-0">or +months</span>
                          <input type="number" value={m.months_from_booking} onChange={e => setMile(i, 'months_from_booking', e.target.value)} placeholder="mo." className="inp w-16" />
                        </label>
                        <label className="text-xs text-gray-600 flex items-center gap-1.5 whitespace-nowrap"><input type="checkbox" checked={m.is_handover} onChange={e => setMile(i, 'is_handover', e.target.checked)} />Handover</label>
                        <label className="text-xs text-gray-600 flex items-center gap-1.5 whitespace-nowrap" title="Post-Handover Payment Plan — paid after handover"><input type="checkbox" checked={m.is_post_handover} onChange={e => setMile(i, 'is_post_handover', e.target.checked)} />PHPP</label>
                      </div>
                      {/* line 3: actions */}
                      <div className="flex flex-wrap items-center gap-1.5 text-xs">
                        <button type="button" onClick={() => moveMile(i, -1)} disabled={i === 0} title="Move up" className="px-1.5 py-1 rounded border border-gray-200 text-gray-500 disabled:opacity-30 hover:bg-gray-50">↑</button>
                        <button type="button" onClick={() => moveMile(i, 1)} disabled={i === form.payment_plans.length - 1} title="Move down" className="px-1.5 py-1 rounded border border-gray-200 text-gray-500 disabled:opacity-30 hover:bg-gray-50">↓</button>
                        <button type="button" onClick={() => duplicateMile(i)} title="Duplicate (+1 month)" className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50">⧉ Duplicate</button>
                        <button type="button" onClick={() => duplicateNMile(i)} title="Duplicate ×N monthly (e.g. 1% × 24)" className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50">×N</button>
                        <button type="button" onClick={() => setForm({ ...form, payment_plans: form.payment_plans.filter((_, x) => x !== i) })} title="Remove" className="ml-auto px-2 py-1 rounded border border-red-100 text-red-400 hover:bg-red-50">🗑 Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
                {form.is_phpp && !anyPhppMilestone && (
                  <p className="mt-3 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">⚠ No PHPP milestones defined yet — mark the post-handover instalments with the PHPP checkbox.</p>
                )}
                {!form.is_phpp && anyPhppMilestone && (
                  <p className="mt-3 text-xs text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 flex items-center gap-2">
                    Some milestones are marked PHPP.
                    <button type="button" onClick={() => setForm({ ...form, is_phpp: true })} className="underline font-medium">Enable “PHPP project”?</button>
                  </p>
                )}
              </div>

              <div className="flex gap-3 pb-6">
                <button onClick={save} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-medium">{saving ? 'Saving…' : (editId ? 'Save Changes' : 'Create Project')}</button>
                <button onClick={() => setShowForm(false)} className="px-5 bg-white border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`.inp{width:100%;border:1px solid #e5e7eb;border-radius:0.5rem;padding:0.4rem 0.6rem;font-size:0.85rem;outline:none;background:#fff}.inp:focus{box-shadow:0 0 0 2px #86efac}@keyframes fadeIn{from{opacity:0;transform:translate(-50%,6px)}to{opacity:1;transform:translate(-50%,0)}}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs text-gray-500 block mb-1">{label}</span>{children}</label>;
}
function StrInput({ label, value, onChange, hint }) {
  return (
    <div>
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      <input type="number" value={value} onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white" />
      {hint && <span className="text-[10px] text-gray-400">{hint}</span>}
    </div>
  );
}
const MATURITY_OPTIONS = [3, 5, 7, 10, 15, 20];
function MaturitySelect({ value, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-gray-700">Investment maturity <span className="text-xs text-gray-400">(years from handover)</span></p>
      <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
        {MATURITY_OPTIONS.map(y => (
          <button key={y} type="button" onClick={() => onChange(y)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${value === y ? 'bg-primary text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>{y}y</button>
        ))}
      </div>
    </div>
  );
}
function BandRow({ label, bands, setBands }) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-gray-700">{label}</p>
      <div className="flex items-center gap-1">
        {bands.map((b, i) => (
          <span key={i} className="flex items-center">
            <input type="number" value={b} onChange={e => setBands(bands.map((x, idx) => idx === i ? Number(e.target.value) : x))}
              className="w-12 border border-gray-200 rounded-lg px-1 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-green-500" />
            {i < 2 && <span className="text-gray-300 mx-0.5">/</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

// AED→ILS exchange-rate control: Live (fetched) vs Manual (typed, ILS per 1 AED).
// Manual pins the rate applied to every ₪ figure on the page and into share snapshots.
function ExchangeRateControl({ rateMode, onLive, onManual, manualIls, setManualIls, liveIls, ilsLive, outOfBounds }) {
  const manual = rateMode === 'manual';
  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-gray-700">Exchange rate <span className="text-xs text-gray-400">(₪ per 1 AED)</span></p>
        <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
          <button type="button" onClick={onLive}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${!manual ? 'bg-primary text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Live</button>
          <button type="button" onClick={onManual}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${manual ? 'bg-amber-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Manual</button>
        </div>
      </div>
      {manual ? (
        <div className="mt-2">
          <div className="flex items-center justify-end gap-1">
            <span className="text-xs text-gray-400">1 AED =</span>
            <input type="number" step="0.0001" value={manualIls} placeholder="0.8150"
              onChange={e => setManualIls(e.target.value)}
              className={`w-28 border rounded-lg px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 ${outOfBounds ? 'border-amber-400 ring-1 ring-amber-300 focus:ring-amber-400' : 'border-gray-200 focus:ring-green-500'}`} />
            <span className="text-xs text-gray-400">₪</span>
          </div>
          {outOfBounds && <p className="text-[11px] text-amber-600 mt-1 text-right">Unusual rate — AED→ILS is typically 0.5–1.5. Double-check before sharing.</p>}
        </div>
      ) : (
        <p className="text-[11px] text-gray-400 mt-1 text-right">Using {ilsLive ? 'live' : 'fallback'} rate 1 AED = ₪{Number(liveIls).toFixed(4)}</p>
      )}
    </div>
  );
}
